import { StandardEvents } from '@shopify/events';

/**
 * Hugh: cart changes answer at once (owner, 21 Oct 2026).
 *
 * The store's cart API and the pack discount Function take about half a second per change (measured
 * 21 Oct 2026, the same on the live and the dev theme), so the cart shows that it is working
 * straight away instead of waiting in silence:
 * - an add opens the cart drawer on the click, also when the cart was empty (Horizon waits for the
 *   server then); an empty drawer reads "Adding to your cart…" until the line arrives;
 * - while any line change is pending (add, quantity, remove), every cart region carries
 *   [data-hugh-busy]: a thin progress bar runs and the totals and tier bar dim
 *   (snippets/hugh-cart-styles.liquid). Quantity and remove taps start it on the tap.
 *
 * Listens to the same standard event Horizon's cart components use; Horizon's files are untouched.
 */

const BUSY = 'data-hugh-busy';
let pending = 0;

/** @returns {NodeListOf<Element>} The cart regions (drawer and cart page). */
const regions = () => document.querySelectorAll('cart-items-component');

/** @returns {(HTMLElement & { isOpen?: boolean, open?: () => void, close?: () => void }) | null} */
const drawer = () => /** @type {any} */ (document.querySelector('cart-drawer-component[auto-open]')?.closest('theme-drawer') ?? null);

/**
 * Opens the drawer on an add unless it is open already or the add came from a modal dialog
 * (quick add), whose own close restores focus first; Horizon handles that case.
 * @param {Event} event
 * @returns {boolean} Whether this call opened it.
 */
function openOnAdd(event) {
  const target = drawer();
  if (!target || target.isOpen || typeof target.open !== 'function') return false;
  if (event.target instanceof Element && event.target.closest('dialog:modal')) return false;
  target.open();
  return true;
}

/** @type {ReturnType<typeof setTimeout> | undefined} */
let armed;

/** Clears the busy state when no change is pending. */
const settle = () => {
  if (!pending) document.querySelectorAll(`[${BUSY}]`).forEach((region) => region.removeAttribute(BUSY));
};

// Horizon waits a moment before it sends quantity changes (it batches quick taps), so the busy
// state starts on the tap itself. If no change follows (e.g. − at 1), it clears after 1.5 s.
document.addEventListener('click', (event) => {
  const control = event.target instanceof Element ? event.target.closest('.quantity-minus, .quantity-plus, .cart-items__remove') : null;
  if (!control?.closest('cart-items-component')) return;
  regions().forEach((region) => region.setAttribute(BUSY, ''));
  clearTimeout(armed);
  armed = setTimeout(settle, 1500);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {Event & { action?: string, promise?: Promise<any> }} */ (event);
  clearTimeout(armed);
  pending += 1;
  regions().forEach((region) => region.setAttribute(BUSY, ''));
  const opened = update.action === 'add' && openOnAdd(event);

  /** @param {any} [result] */
  const done = (result) => {
    pending = Math.max(0, pending - 1);
    settle();
    // A failed add into an empty cart leaves nothing to show: close the drawer this opened.
    const emptyCart = !result?.cart?.lines?.length && !result?.detail?.items?.length;
    if (opened && result?.detail?.didError && emptyCart) drawer()?.close?.();
  };
  Promise.resolve(update.promise).then(done, () => done());
});
